import * as React from 'react';

const sameValue = Object.is ?? ((left, right) =>
    left === right ? left !== 0 || 1 / left === 1 / right : left !== left && right !== right
);
const isReactNative =
    typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
const canUseLayoutEffect = typeof window !== 'undefined' || isReactNative;
const useIsomorphicLayoutEffect = canUseLayoutEffect
    ? React.useLayoutEffect
    : React.useEffect;

function snapshotChanged(instance) {
    try {
        return !sameValue(instance.value, instance.getSnapshot());
    } catch {
        return true;
    }
}

function useSyncExternalStoreFallback(subscribe, getSnapshot, getServerSnapshot) {
    const isServer = typeof window === 'undefined' && !isReactNative;
    const value = isServer && typeof getServerSnapshot === 'function'
        ? getServerSnapshot()
        : getSnapshot();
    const [{ instance }, forceUpdate] = React.useState(() => ({
        instance: { value, getSnapshot },
    }));

    useIsomorphicLayoutEffect(() => {
        instance.value = value;
        instance.getSnapshot = getSnapshot;
        if (snapshotChanged(instance)) forceUpdate({ instance });
    }, [getSnapshot, value]);

    React.useEffect(() => {
        if (snapshotChanged(instance)) forceUpdate({ instance });
        return subscribe(() => {
            if (snapshotChanged(instance)) forceUpdate({ instance });
        });
    }, [subscribe]);

    React.useDebugValue(value);
    return value;
}

export function useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) {
    const nativeHook = React.useSyncExternalStore;
    if (typeof nativeHook === 'function') {
        return nativeHook(subscribe, getSnapshot, getServerSnapshot);
    }
    return useSyncExternalStoreFallback(subscribe, getSnapshot, getServerSnapshot);
}
